import { Trash2 } from 'lucide-react';
import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ActionButton } from './ActionButton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/Tooltip';

interface DeleteDialogProps {
  onDelete: () => void;
  isBulkOperation?: boolean;
  disabled?: boolean;
  disabledReason?: string | null;
  loading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectedCount?: number;
}

export const DeleteDialog: React.FC<DeleteDialogProps> = ({
  onDelete,
  isBulkOperation = false,
  disabled = false,
  disabledReason = null,
  loading = false,
  open,
  onOpenChange,
  selectedCount = 1,
}) => {
  const description =
    isBulkOperation && selectedCount > 1
      ? `Möchten Sie wirklich ${selectedCount} Vorgänge löschen? Gelöschte Vorgänge können nicht bearbeitet, gespeichert oder verschickt werden. Sie haben die Möglichkeit, die Vorgänge wiederherzustellen.`
      : 'Gelöschte Vorgänge können nicht bearbeitet, gespeichert oder verschickt werden. Sie haben die Möglichkeit, den Vorgang wiederherzustellen.';

  const content = (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Sind Sie wirklich sicher?</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
        <AlertDialogAction
          onClick={onDelete}
          className="bg-red-500 hover:bg-red-600 text-white"
        >
          Löschen
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );

  if (isBulkOperation) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        {content}
      </AlertDialog>
    );
  }

  let trigger: React.ReactNode = (
    <ActionButton
      disabled={disabled}
      loading={loading}
      loadingText="Wird gelöscht..."
      normalText="Löschen"
      icon={Trash2}
      variant="destructive"
    />
  );

  if (disabledReason) {
    trigger = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{trigger}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{disabledReason}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (disabled) {
    return trigger;
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      {content}
    </AlertDialog>
  );
};
